import React, { useState } from 'react';

export default function PaywallModal({ open, analysisReady, onPurchase, onRestore, onClose }) {
  const [billing, setBilling] = useState('monthly');
  const [processing, setProcessing] = useState(null);
  const [checkoutError, setCheckoutError] = useState(null);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreEmail, setRestoreEmail] = useState('');
  const [restoreStatus, setRestoreStatus] = useState(null); // { ok, message }
  const [restoreSending, setRestoreSending] = useState(false);

  if (!open) return null;

  async function buy(planType) {
    if (processing) return;
    setProcessing(planType);
    setCheckoutError(null);
    try {
      // Redirects to Stripe Checkout; the spinner runs until the page unloads.
      await onPurchase(planType);
    } catch (err) {
      setCheckoutError(err.message || 'Could not start checkout. Please try again.');
      setProcessing(null);
    }
  }

  async function submitRestore(e) {
    e.preventDefault();
    if (restoreSending) return;
    setRestoreSending(true);
    setRestoreStatus(null);
    try {
      const message = await onRestore(restoreEmail.trim());
      setRestoreStatus({ ok: true, message });
    } catch (err) {
      setRestoreStatus({ ok: false, message: err.message || 'Restore failed. Please try again.' });
    } finally {
      setRestoreSending(false);
    }
  }

  const proPlan = billing === 'annual' ? 'pro-annual' : 'pro-monthly';
  const proLabel = billing === 'annual' ? 'Go Pro — $180/year' : 'Go Pro — $19.99/month';

  return (
    <div className="paywall-overlay" role="dialog" aria-modal="true" aria-label="Unlock your report">
      <div className="paywall-modal">
        <button className="paywall-close" onClick={onClose} aria-label="Close" disabled={!!processing}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className="paywall-head">
          <div className="paywall-lock">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <h2>{analysisReady ? 'Your report is ready' : 'Your report is almost ready'}</h2>
          <p>
            {analysisReady
              ? 'Unlock it to view the full breakdown of risks, fees and questions to ask.'
              : 'While we finish reading your document, choose how you’d like to unlock the full report.'}
          </p>
        </div>

        <div className="paywall-plans">
          <div className="paywall-card">
            <h3>Single Report</h3>
            <div className="paywall-price">
              $2.99<span className="paywall-price-unit"> one-off</span>
            </div>
            <p className="paywall-price-sub">This document only</p>
            <ul className="paywall-features">
              <li>Full risk breakdown</li>
              <li>Fees &amp; agent questions</li>
              <li>Plan diagram with your lot circled</li>
            </ul>
            <button
              className="paywall-btn paywall-btn-secondary"
              onClick={() => buy('single')}
              disabled={!!processing}
            >
              {processing === 'single' ? <span className="paywall-spinner" /> : 'Unlock this report — $2.99'}
            </button>
          </div>

          <div className="paywall-card paywall-card-pro">
            <div className="paywall-badge">Most popular</div>
            <h3>Pro Plan</h3>

            <div className="billing-toggle" role="tablist" aria-label="Billing period">
              <button
                role="tab"
                aria-selected={billing === 'monthly'}
                className={billing === 'monthly' ? 'billing-active' : ''}
                onClick={() => setBilling('monthly')}
                disabled={!!processing}
              >
                Monthly
              </button>
              <button
                role="tab"
                aria-selected={billing === 'annual'}
                className={billing === 'annual' ? 'billing-active' : ''}
                onClick={() => setBilling('annual')}
                disabled={!!processing}
              >
                Annual <span className="billing-save">save 25%</span>
              </button>
            </div>

            {billing === 'monthly' ? (
              <>
                <div className="paywall-price">
                  $19.99<span className="paywall-price-unit">/mo</span>
                </div>
                <p className="paywall-price-sub">Billed monthly · cancel anytime</p>
              </>
            ) : (
              <>
                <div className="paywall-price">
                  $14.99<span className="paywall-price-unit">/mo</span>
                </div>
                <p className="paywall-price-sub">Billed annually as $180</p>
              </>
            )}

            <ul className="paywall-features">
              <li>{billing === 'annual' ? '200+ Section 32/Contract of Sale reports a year' : '20 Section 32/Contract of Sale reports every month'}</li>
              <li>Every risk, fee &amp; question breakdown</li>
              <li>Plan diagrams with your lot circled</li>
              <li>Full property history</li>
            </ul>
            <button
              className="paywall-btn paywall-btn-primary"
              onClick={() => buy(proPlan)}
              disabled={!!processing}
            >
              {processing === proPlan ? <span className="paywall-spinner" /> : proLabel}
            </button>
          </div>
        </div>

        {checkoutError && <p className="paywall-error" role="alert">{checkoutError}</p>}

        <div className="paywall-restore">
          {!restoreOpen ? (
            <button className="paywall-restore-link" onClick={() => setRestoreOpen(true)}>
              Already Pro? Sign in with your email
            </button>
          ) : (
            <form className="paywall-restore-form" onSubmit={submitRestore}>
              <input
                type="email"
                required
                placeholder="Email used at checkout"
                value={restoreEmail}
                onChange={e => setRestoreEmail(e.target.value)}
                disabled={restoreSending}
              />
              <button type="submit" className="paywall-btn paywall-btn-secondary" disabled={restoreSending}>
                {restoreSending ? <span className="paywall-spinner" /> : 'Send sign-in link'}
              </button>
            </form>
          )}
          {restoreStatus && (
            <p className={restoreStatus.ok ? 'paywall-restore-ok' : 'paywall-error'} role="status">
              {restoreStatus.message}
            </p>
          )}
        </div>

        <p className="paywall-footnote">Secure checkout powered by Stripe. Cancel anytime.</p>
      </div>
    </div>
  );
}
